import type {
  BodyStockpile,
  Cargo,
  DevelopmentLevel,
  GameLogEntry,
  GameState,
  MaterialId,
} from "../shared/game-types";
import type { Cost } from "../game-data/catalog";

export const MATERIAL_IDS: readonly MaterialId[] = ["structural", "highTemp", "nuclear", "superconductor", "rare"];
export const FUEL_IDS = ["chemicalFuel", "fissionFuel", "fusionFuel", "antimatter", "propellant"] as const;

const BODY_ENERGY_CAPACITY = [0, 250, 900, 1_600, 3_000, 5_200, 8_000] as const;
const BODY_MATERIAL_CAPACITY = [0, 400, 1_300, 2_400, 4_500, 7_500, 12_000] as const;

export class GameRuleError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = "GameRuleError";
  }
}

export function requireRule(condition: unknown, code: string, message: string): asserts condition {
  if (!condition) throw new GameRuleError(code, message);
}

export function cloneState(state: GameState): GameState {
  return structuredClone(state);
}

export function finite(value: number, fallback = 0): number {
  return Number.isFinite(value) ? value : fallback;
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, finite(value, minimum)));
}

export function round(value: number, places = 6): number {
  const factor = 10 ** places;
  return Math.round(finite(value) * factor) / factor;
}

export function nextStableId(state: GameState, prefix: string): string {
  const sequence = state.meta.eventSequence;
  state.meta.eventSequence += 1;
  return `${prefix}_${sequence.toString(36).padStart(6, "0")}`;
}

export function addLog(
  state: GameState,
  category: GameLogEntry["category"],
  title: string,
  detail: string,
  important = false,
): void {
  state.events.log.unshift({
    id: nextStableId(state, "log"),
    atGameYear: round(state.meta.gameTimeYears),
    category,
    title,
    detail,
    ...(important ? { important: true } : {}),
  });
  if (state.events.log.length > 400) state.events.log.length = 400;
}

export function cargoTotal(cargo: Cargo): number {
  let total = Math.max(0, finite(cargo.energy ?? 0));
  for (const material of MATERIAL_IDS) total += Math.max(0, finite(cargo.materials?.[material] ?? 0));
  return total;
}

export function validateCargo(cargo: Cargo): void {
  requireRule((cargo.energy ?? 0) >= 0 && Number.isFinite(cargo.energy ?? 0), "INVALID_CARGO", "能源货物必须是非负有限数值");
  for (const amount of Object.values(cargo.materials ?? {})) {
    requireRule((amount ?? 0) >= 0 && Number.isFinite(amount ?? 0), "INVALID_CARGO", "材料货物必须是非负有限数值");
  }
  requireRule(cargoTotal(cargo) > 0, "EMPTY_CARGO", "货物不能为空");
}

export function emptyMaterials(): Record<MaterialId, number> {
  return { structural: 0, highTemp: 0, nuclear: 0, superconductor: 0, rare: 0 };
}

export function createBodyStockpile(level: DevelopmentLevel): BodyStockpile {
  return {
    energy: 0,
    energyCapacity: BODY_ENERGY_CAPACITY[level] ?? 0,
    materials: emptyMaterials(),
    materialCapacity: BODY_MATERIAL_CAPACITY[level] ?? 0,
  };
}

export function ensureBodyStockpile(state: GameState, bodyId: string): BodyStockpile {
  const development = state.developments[bodyId];
  requireRule(development, "UNKNOWN_STOCKPILE", "该天体尚未建立可用库存");
  development.stockpile ??= createBodyStockpile(development.level);
  return development.stockpile;
}

export function expandBodyStockpile(development: GameState["developments"][string]): void {
  const defaults = createBodyStockpile(development.level);
  development.stockpile.energyCapacity = Math.max(development.stockpile.energyCapacity, defaults.energyCapacity);
  development.stockpile.materialCapacity = Math.max(development.stockpile.materialCapacity, defaults.materialCapacity);
}

export function homeBodyId(state: GameState): string {
  const home = Object.values(state.developments).sort((left, right) => right.level - left.level)[0];
  requireRule(home, "NO_HOME_BODY", "没有可用母星库存");
  return home.bodyId;
}

export function canAffordCargoAt(state: GameState, bodyId: string, cargo: Cargo): boolean {
  const stockpile = state.developments[bodyId]?.stockpile;
  if (!stockpile || (cargo.energy ?? 0) > stockpile.energy + 1e-9) return false;
  return MATERIAL_IDS.every((material) => (cargo.materials?.[material] ?? 0) <= stockpile.materials[material] + 1e-9);
}

export function incomingCargoReservation(state: GameState, bodyId: string, excludeMissionId?: string): Cargo {
  const materials = emptyMaterials();
  let energy = 0;
  for (const mission of state.missions) {
    if (
      mission.id === excludeMissionId
      || mission.type !== "transport"
      || mission.status !== "enRoute"
      || mission.targetBodyId !== bodyId
      || !mission.cargo
    ) continue;
    energy += Math.max(0, finite(mission.cargo.energy ?? 0));
    for (const material of MATERIAL_IDS) {
      materials[material] += Math.max(0, finite(mission.cargo.materials?.[material] ?? 0));
    }
  }
  return { energy: round(energy), materials };
}

export function cargoFitsAt(state: GameState, bodyId: string, cargo: Cargo, excludeMissionId?: string): boolean {
  const stockpile = state.developments[bodyId]?.stockpile;
  if (!stockpile) return false;
  const reserved = incomingCargoReservation(state, bodyId, excludeMissionId);
  const materialTotal = MATERIAL_IDS.reduce((sum, material) => (
    sum + stockpile.materials[material] + (reserved.materials?.[material] ?? 0) + (cargo.materials?.[material] ?? 0)
  ), 0);
  return stockpile.energy + (reserved.energy ?? 0) + (cargo.energy ?? 0) <= stockpile.energyCapacity + 1e-9
    && materialTotal <= stockpile.materialCapacity + 1e-9;
}

export function removeCargoAt(state: GameState, bodyId: string, cargo: Cargo): void {
  requireRule(canAffordCargoAt(state, bodyId, cargo), "INSUFFICIENT_LOCAL_RESOURCES", "出发星球的本地库存不足");
  const stockpile = ensureBodyStockpile(state, bodyId);
  stockpile.energy = round(stockpile.energy - (cargo.energy ?? 0));
  for (const material of MATERIAL_IDS) stockpile.materials[material] = round(stockpile.materials[material] - (cargo.materials?.[material] ?? 0));
  syncResourceAggregates(state);
}

export function addCargoAt(state: GameState, bodyId: string, cargo: Cargo, excludeMissionId?: string): void {
  requireRule(cargoFitsAt(state, bodyId, cargo, excludeMissionId), "DESTINATION_CAPACITY", "目标星球仓储空间不足");
  const stockpile = ensureBodyStockpile(state, bodyId);
  stockpile.energy = round(stockpile.energy + (cargo.energy ?? 0));
  for (const material of MATERIAL_IDS) stockpile.materials[material] = round(Math.max(0, stockpile.materials[material] + (cargo.materials?.[material] ?? 0)));
  syncResourceAggregates(state);
}

export function canAffordCostAt(state: GameState, bodyId: string, cost: Cost): boolean {
  return canAffordCargoAt(state, bodyId, costAsCargo(cost));
}

export function payCostAt(state: GameState, bodyId: string, cost: Cost): void {
  removeCargoAt(state, bodyId, costAsCargo(cost));
}

export function canAffordCargo(state: GameState, cargo: Cargo): boolean {
  return canAffordCargoAt(state, homeBodyId(state), cargo);
}

export function removeCargo(state: GameState, cargo: Cargo): void {
  removeCargoAt(state, homeBodyId(state), cargo);
}

export function addCargo(state: GameState, cargo: Cargo): void {
  addCargoAt(state, homeBodyId(state), cargo);
}

export function costAsCargo(cost: Cost): Cargo {
  return { energy: cost.energy, materials: cost.materials };
}

export function canAffordCost(state: GameState, cost: Cost): boolean {
  return canAffordCostAt(state, homeBodyId(state), cost);
}

export function payCost(state: GameState, cost: Cost): void {
  payCostAt(state, homeBodyId(state), cost);
}

export function enforceMaterialCapacityAt(stockpile: BodyStockpile): void {
  const total = MATERIAL_IDS.reduce((sum, material) => sum + Math.max(0, finite(stockpile.materials[material])), 0);
  const capacity = Math.max(0, finite(stockpile.materialCapacity));
  if (total <= capacity || total === 0) return;
  const scale = capacity / total;
  for (const material of MATERIAL_IDS) stockpile.materials[material] = round(stockpile.materials[material] * scale);
}

export function syncResourceAggregates(state: GameState): void {
  state.resources.energy.available = 0;
  state.resources.energy.capacity = 0;
  state.resources.materials = emptyMaterials();
  state.resources.materialCapacity = 0;
  for (const development of Object.values(state.developments)) {
    if (!development.stockpile) continue;
    state.resources.energy.available += development.stockpile.energy;
    state.resources.energy.capacity += development.stockpile.energyCapacity;
    state.resources.materialCapacity += development.stockpile.materialCapacity;
    for (const material of MATERIAL_IDS) state.resources.materials[material] += development.stockpile.materials[material];
  }
  state.resources.energy.available = round(state.resources.energy.available);
  state.resources.energy.capacity = round(state.resources.energy.capacity);
  state.resources.materialCapacity = round(state.resources.materialCapacity);
  for (const material of MATERIAL_IDS) state.resources.materials[material] = round(state.resources.materials[material]);
}

export function enforceMaterialCapacity(state: GameState): void {
  for (const development of Object.values(state.developments)) enforceMaterialCapacityAt(development.stockpile);
  syncResourceAggregates(state);
}

export function sanitizeStateNumbers(state: GameState): void {
  const energy = state.resources.energy;
  for (const key of ["chemicalFuel", "fissionFuel", "fusionFuel", "antimatter", "propellant", "available", "capacity", "productionPerYear", "maintenancePerYear"] as const) {
    energy[key] = Math.max(0, finite(energy[key]));
  }
  energy.available = Math.min(energy.available, energy.capacity);
  energy.fuelProductionPerYear ??= { chemicalFuel: 0, fissionFuel: 0, fusionFuel: 0, antimatter: 0, propellant: 0 };
  energy.fuelConsumptionPerYear ??= { chemicalFuel: 0, fissionFuel: 0, fusionFuel: 0, antimatter: 0, propellant: 0 };
  for (const fuel of FUEL_IDS) {
    energy.fuelProductionPerYear[fuel] = Math.max(0, finite(energy.fuelProductionPerYear[fuel]));
    energy.fuelConsumptionPerYear[fuel] = Math.max(0, finite(energy.fuelConsumptionPerYear[fuel]));
  }
  for (const material of MATERIAL_IDS) state.resources.materials[material] = Math.max(0, finite(state.resources.materials[material]));
  state.resources.materialCapacity = Math.max(0, finite(state.resources.materialCapacity));
  state.resources.research = clamp(state.resources.research, 0, Math.max(0, finite(state.resources.researchCapacity)));
  state.resources.researchPerYear = Math.max(0, finite(state.resources.researchPerYear));
  state.meta.gameTimeYears = Math.max(0, finite(state.meta.gameTimeYears));
  for (const development of Object.values(state.developments)) {
    development.stockpile ??= createBodyStockpile(development.level);
    expandBodyStockpile(development);
    development.stockpile.energy = clamp(development.stockpile.energy, 0, development.stockpile.energyCapacity);
    development.stockpile.materialCapacity = Math.max(0, finite(development.stockpile.materialCapacity));
    for (const material of MATERIAL_IDS) development.stockpile.materials[material] = Math.max(0, finite(development.stockpile.materials[material]));
    enforceMaterialCapacityAt(development.stockpile);
    development.constructionQueue ??= [];
    development.condition = clamp(development.condition, 0, 100);
    development.exploitationYears = Math.max(0, finite(development.exploitationYears));
  }
  for (const ship of state.ships) {
    ship.hull = clamp(ship.hull, 0, 100);
    ship.propulsion = clamp(ship.propulsion, 0, 100);
    ship.sensors = clamp(ship.sensors, 0, 100);
    ship.weapons = clamp(ship.weapons, 0, 100);
    ship.fuel = Math.max(0, finite(ship.fuel));
  }
  for (const record of Object.values(state.diplomacy)) {
    record.affinity = clamp(record.affinity, 0, 100);
    record.borderTension = clamp(record.borderTension, 0, 100);
    record.militaryAttrition = clamp(record.militaryAttrition ?? 0, 0, 85);
    record.contestedBodyIds ??= [];
  }
  syncResourceAggregates(state);
}
